import { useState, useCallback } from "react";
import { useAuthenticatedFetchWithWorkspace } from "../../Keycloak/useAuthenticatedFetchWithWorkspace";
import { ImportedList } from "../../../pages/Clients/ListImport/types";
import { useQueryBuilderCache } from "../../../context/QueryBuilderCacheContext";
import { SEGMENT_FIELDS_CACHE_PREFIX } from "../../QueryBuilder/useSegmentFields";

interface GetCustomersListParams {
    page?: number;
    itemsPerPage?: number;
    forceRefresh?: boolean;
}

interface GetCustomersListResponse {
    totalItems: number;
    member: ImportedList[];
}

const LISTS_CACHE_PREFIX = 'customers_list|';

const useGetCustomersList = () => {
    const { authFetch, isReady } = useAuthenticatedFetchWithWorkspace();
    const cache = useQueryBuilderCache();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string>("");
    const [data, setData] = useState<GetCustomersListResponse | null>(null);

    const getCustomersList = useCallback(async (params: GetCustomersListParams = {}): Promise<GetCustomersListResponse | null> => {
        const { page = 1, itemsPerPage = 10, forceRefresh = false } = params;
        const cacheKey = `${LISTS_CACHE_PREFIX}${page}|${itemsPerPage}`;

        if (!forceRefresh) {
            const hit = cache?.get<GetCustomersListResponse>(cacheKey);
            if (hit) {
                setData(hit.data);
                setLoading(false);
                return hit.data;
            }
        }

        setLoading(true);
        setError("");
        try {
            const queryParams = new URLSearchParams({
                page: page.toString(),
                itemsPerPage: itemsPerPage.toString(),
            });
            const result: GetCustomersListResponse = await authFetch(`/getCustomersList?${queryParams.toString()}`, {
                method: "GET",
            });
            setData(result);
            cache?.set(cacheKey, result);
            return result;
        } catch (err: unknown) {
            if (err instanceof Error) {
                setError(err.message);
            } else {
                setError("An unknown error occurred");
            }
            return null;
        } finally {
            setLoading(false);
        }
    }, [authFetch, cache]);

    // Les listes sont aussi proposées dans le ciblage (critères « liste de clients ») : leurs valeurs sont rechargées avec
    const invalidateListsCache = useCallback(() => {
        cache?.invalidate(LISTS_CACHE_PREFIX);
        cache?.invalidate(SEGMENT_FIELDS_CACHE_PREFIX);
    }, [cache]);

    const { workspaceId } = useAuthenticatedFetchWithWorkspace();

    return { getCustomersList, loading, error, data, invalidateListsCache, isReady, workspaceId };
};

export default useGetCustomersList;
